// 文件预览：在页面里直接看，不下载。图片用看图器（可缩放），PDF 内嵌显示，Word（.docx）用 docx-preview 排版显示；
// 其它格式（.doc / .xls 等）浏览器看不了，给「下载」。先把文件取成 blob 再按扩展名定类型 —— 存储里的类型可能是 octet-stream，直接打开会变成下载
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Lightbox } from "./Lightbox";

export interface PreviewTarget {
  url: string;
  name: string;
  contentType?: string;
}

type Kind = "image" | "pdf" | "docx" | "other";

export function previewKind(name: string, contentType = ""): Kind {
  const ext = name.toLowerCase().match(/\.(\w+)$/)?.[1] ?? "";
  if (contentType.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "image";
  if (contentType === "application/pdf" || ext === "pdf") return "pdf";
  if (ext === "docx" || contentType.includes("wordprocessingml")) return "docx";
  return "other";
}

export function useFilePreview() {
  const [target, setTarget] = useState<PreviewTarget | null>(null);
  const node = target ? <FilePreview target={target} onClose={() => setTarget(null)} /> : null;
  return { open: setTarget, node };
}

export function FilePreview({ target, onClose }: { target: PreviewTarget; onClose: () => void }) {
  if (previewKind(target.name, target.contentType) === "image") return <Lightbox src={target.url} onClose={onClose} />;
  return createPortal(<DocPreview target={target} onClose={onClose} />, document.body);
}

function DocPreview({ target, onClose }: { target: PreviewTarget; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-mask modal-top" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal file-preview" role="dialog" aria-modal="true" aria-label={`预览 ${target.name}`} data-testid="file-preview">
        <div className="row file-preview-bar">
          <b className="cell-clip" title={target.name} translate="no">{target.name}</b>
          <span className="spacer" />
          <a className="btn btn-sm" href={target.url} target="_blank" rel="noreferrer" download={target.name}>下载</a>
          <button className="btn btn-sm" onClick={onClose} aria-label="关闭预览">✕ 关闭</button>
        </div>
        <FileView target={target} />
      </div>
    </div>
  );
}

/**
 * 内嵌显示一份文件（预览弹窗、压缩件对比都用它）。名字看不出类型时（压缩副本）按服务器回的 Content-Type 定
 */
export function FileView({ target }: { target: PreviewTarget }) {
  const box = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<{ kind: Kind; url?: string } | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let alive = true;
    let blobUrl: string | null = null;
    setState("loading");
    setView(null);
    void (async () => {
      try {
        const res = await fetch(target.url);
        if (!res.ok) throw new Error(String(res.status));
        const raw = await res.blob();
        let kind = previewKind(target.name, target.contentType);
        if (kind === "other") kind = previewKind("", raw.type);
        if (!alive) return;
        if (kind === "image" || kind === "pdf") {
          blobUrl = URL.createObjectURL(kind === "pdf" ? new Blob([raw], { type: "application/pdf" }) : raw);
          setView({ kind, url: blobUrl });
        } else if (kind === "docx") {
          setView({ kind });
          const { renderAsync } = await import("docx-preview");
          // 等 docx 容器挂上
          await new Promise((r) => requestAnimationFrame(r));
          if (!alive || !box.current) return;
          await renderAsync(raw, box.current, undefined, { inWrapper: true, ignoreLastRenderedPageBreak: true });
        } else {
          setView({ kind });
          setState("error");
          return;
        }
        if (alive) setState("ready");
      } catch {
        if (alive) setState("error");
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [target.url, target.name, target.contentType]);

  return (
    <>
      {state === "loading" && <p className="muted">正在打开…</p>}
      {state === "error" && <p className="notice">{view?.kind === "other" ? "这种格式不能在网页里预览，请下载后用电脑打开。" : "预览打不开，请下载后查看。"}</p>}
      {view?.kind === "pdf" && view.url && <iframe className="file-preview-frame" src={view.url} title={target.name} />}
      {view?.kind === "image" && view.url && <div className="file-preview-img"><img src={view.url} alt={target.name} /></div>}
      {view?.kind === "docx" && <div ref={box} className="file-preview-docx" hidden={state === "error"} />}
    </>
  );
}
